import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Server, FolderKanban, Box, Globe, ShieldCheck, Cloud, ChevronRight, Plus, LayoutTemplate,
  Upload, Archive, ScrollText, Network, Rocket, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import {
  StatCard, StatusBadge, MetricChart, ContainerDonut, ServerCard, LogViewer,
  IntegrationCard, Panel, ProjectAvatar, AsyncBoundary, CardSkeleton, RowsSkeleton,
} from "@/components/common";
import { TONES } from "@/lib/status";

const ICONS = { server: Server, folder: FolderKanban, box: Box, globe: Globe, shield: ShieldCheck, cloud: Cloud };

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const date = now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const time = now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return (
    <div className="rounded-xl border border-line bg-surface px-4 py-3 text-right">
      <p className="text-xs capitalize text-muted-foreground">{date}</p>
      <div className="mt-1 flex items-center justify-end gap-3">
        <span className="font-heading text-2xl font-bold tabular-nums text-foreground">{time}</span>
        
      </div>
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, onClick, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={disabled ? "Integração ainda não configurada" : undefined}
    data-testid={`quick-action-${label}`}
    className="flex flex-col items-center justify-center gap-2 rounded-lg border border-line bg-surface/60 p-3 text-center transition-all enabled:hover:-translate-y-0.5 enabled:hover:border-brand/30 enabled:hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
  >
    <Icon className="h-5 w-5 text-brand" />
    <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
  </button>
);

export default function Dashboard() {
  const {user}=useAuth();
  const navigate=useNavigate();
  const { data, loading, error } = useAsync(() =>
    Promise.all([api.getDashboardSummary(), api.getLogs()])
      .then(([summary, logs]) => ({ ...summary, logs })),
  );

  return (
    <div className="space-y-6 animate-fade-up">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Olá, {user?.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Aqui está o status da sua infraestrutura hoje.</p>
        </div>
        <Clock />
      </div>

      {/* Stat cards */}
      {loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          {data?.stats.map((s) => (
            <StatCard key={s.key} {...s} icon={ICONS[s.icon]} />
          ))}
        </div>
      )}

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton rows={8} />}>
        {data && (
          <>
            {/* Row 1: usage / servers / containers */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
              <Panel title="Uso da Infraestrutura" className="lg:col-span-2 xl:col-span-5">
                <MetricChart dataByRange={data.usage} height={300} />
                {!Object.values(data.usage || {}).some((points) => points?.length) && <p className="mt-2 text-xs text-muted-foreground" data-testid="dashboard-usage-snapshot-note">Histórico de métricas ainda não é coletado — os valores abaixo são um snapshot atual reportado pelo Agent.</p>}
                <div className="mt-4 grid grid-cols-3 gap-2 border-t border-line/60 pt-4 text-xs">
                  <div><p className="text-muted-foreground">CPU snapshot</p><p className="mt-1 font-mono text-foreground">{data.usage_snapshot?.cpu_avg_pct == null ? "—" : `${data.usage_snapshot.cpu_avg_pct.toFixed(1)}%`}</p></div>
                  <div><p className="text-muted-foreground">RAM</p><p className="mt-1 font-mono text-foreground">{data.usage_snapshot?.ram_total_mb ? `${(data.usage_snapshot.ram_used_mb / 1024).toFixed(1)} / ${(data.usage_snapshot.ram_total_mb / 1024).toFixed(1)} GB` : "—"}</p></div>
                  <div><p className="text-muted-foreground">Disco</p><p className="mt-1 font-mono text-foreground">{data.usage_snapshot?.disk_total_gb ? `${data.usage_snapshot.disk_used_gb.toFixed(1)} / ${data.usage_snapshot.disk_total_gb.toFixed(1)} GB` : "—"}</p></div>
                </div>
              </Panel>

              <Panel
                title="Servidores"
                className="xl:col-span-4"
                action={<Link to="/servidores" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand">Ver todos <ChevronRight className="h-3.5 w-3.5" /></Link>}
              >
                <div className="space-y-3">
                  {data.servers.slice(0, 2).map((s) => <ServerCard key={s.id} server={s} compact />)}
                </div>
              </Panel>

              <Panel title="Status dos Containers" className="xl:col-span-3" action={<span className="text-xs text-muted-foreground">{data.container_inventory?.live_running ?? data.containers.running} ativos · {data.container_inventory?.stale ?? 0} stale</span>}>
                <ContainerDonut summary={data.containers} />
                <p className="mt-4 text-center text-xs text-muted-foreground">{data.container_inventory?.current ?? data.containers.total} atuais observados · {data.container_inventory?.registered ?? data.containers.total} registrados</p>
              </Panel>
            </div>

            {/* Row 2: projects / domains / quick actions */}
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-12">
              <Panel
                title="Projetos Recentes"
                className="xl:col-span-5"
                noPadding
                action={<Link to="/projetos" className="inline-flex items-center gap-1 pr-1 text-xs text-muted-foreground hover:text-brand">Ver todos <ChevronRight className="h-3.5 w-3.5" /></Link>}
              >
                <div className="divide-y divide-line/60">
                  {data.projects.slice(0, 5).map((p) => (
                    <Link key={p.id} to={`/projetos/${p.id}`} className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/[0.03]" data-testid={`dash-project-${p.id}`}>
                      <ProjectAvatar name={p.name} size="sm" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{p.name}</p>
                        <p className="truncate text-xs text-muted-foreground">{p.domains[0]}</p>
                      </div>
                      <span className="hidden text-xs text-muted-foreground sm:block">{p.client}</span>
                      <StatusBadge status={p.status} size="sm" />
                    </Link>
                  ))}
                </div>
              </Panel>

              <Panel
                title="Alertas de domínios"
                className="xl:col-span-4"
                noPadding
                action={<Link to="/dominios" className="inline-flex items-center gap-1 pr-1 text-xs text-muted-foreground hover:text-brand">Ver todos <ChevronRight className="h-3.5 w-3.5" /></Link>}
              >
                <div className="divide-y divide-line/60">
                  {data.alerts?.length ? data.alerts.slice(0, 5).map((alert) => (
                    <div key={`${alert.domain}-${alert.message}`} className="flex items-center gap-3 px-4 py-3">
                      <span className={`h-2 w-2 shrink-0 rounded-full ${TONES[alert.status === "error" ? "err" : "warn"].dot}`} />
                      <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">{alert.domain}</span>
                      <span className="truncate text-xs text-muted-foreground">{alert.message}</span>
                    </div>
                  )) : <p className="px-4 py-4 text-sm text-muted-foreground">Nenhum alerta comprovado.</p>}
                </div>
              </Panel>

              <Panel title="Ações Rápidas" className="lg:col-span-2 xl:col-span-3">
                <div className="grid grid-cols-3 gap-2">
                  <QuickAction icon={Plus} label="Projeto" onClick={() => navigate("/projetos")} />
                  <QuickAction icon={Globe} label="Domínio" disabled />
                  <QuickAction icon={LayoutTemplate} label="WordPress" disabled />
                  <QuickAction icon={Upload} label="Importar" disabled />
                  <QuickAction icon={Archive} label="Backup" disabled />
                  <QuickAction icon={ScrollText} label="Logs" onClick={() => navigate("/logs")} />
                  <QuickAction icon={Cloud} label="DNS" disabled />
                  <QuickAction icon={ShieldCheck} label="SSL" disabled />
                  <QuickAction icon={Network} label="NPM" disabled />
                </div>
              </Panel>
            </div>

            {/* Row 3: deployments / logs / integrations */}
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-dash-bottom">
              <Panel
                title="Deployments recentes"
                className="min-w-0"
                noPadding
                action={<Link to="/deployments" className="inline-flex items-center gap-1 pr-1 text-xs text-muted-foreground hover:text-brand">Ver todos <ChevronRight className="h-3.5 w-3.5" /></Link>}
              >
                <div className="divide-y divide-line/60">
                  {data.deployments?.length ? data.deployments.slice(0, 5).map((d) => (
                    <div key={d.id} className="flex items-center gap-3 px-4 py-3">
                      <Rocket className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{d.project}</p>
                        <p className="truncate text-xs text-muted-foreground">{d.env} · <span className="font-mono">{d.commit}</span></p>
                      </div>
                      <StatusBadge status={d.status} size="sm" className="shrink-0" />
                      <span className="hidden w-24 shrink-0 text-right text-xs text-muted-foreground xl:block">{d.date}</span>
                    </div>
                  )) : <p className="px-4 py-4 text-sm text-muted-foreground">Nenhum deployment registrado.</p>}
                </div>
              </Panel>

              <div className="min-w-0">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="font-heading text-base font-semibold text-foreground">Auditoria de ações</h2>
                  <Link to="/logs" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-brand">Ver todos <ChevronRight className="h-3.5 w-3.5" /></Link>
                </div>
                <LogViewer logs={data.logs} showFilters={false} height="h-[300px]" title="Audit Log" />
              </div>

              <Panel
                title="Integrações"
                className="min-w-0"
                bodyClassName="space-y-2"
                action={<Link to="/configuracoes" className="text-xs text-muted-foreground hover:text-brand"><ExternalLink className="h-3.5 w-3.5" /></Link>}
              >
                {data.integrations?.length ? data.integrations.map((it) => (
                  <IntegrationCard key={it.id} integration={it} />
                )) : <p className="text-sm text-muted-foreground">Nenhuma integração registrada.</p>}
              </Panel>
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
